import type { CreatedPerson } from '@/components/features/People/forms/AddPersonForm';
import CreatePersonModal from '@/components/features/People/forms/CreatePersonModal';
import { PersonSelect, type PersonOption } from '@/components/global/PersonSelect';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { useCan } from '@/lib/utils';
import axios from 'axios';
import { UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type ApiPerson = {
    uuid: string;
    first_name: string;
    last_name: string;
    email?: string;
    position?: string;
};

interface ContactAsyncSelectProps {
    companyId?: string;
    /**
     * Resolves the current value's label without waiting on the company's roster. A
     * lead's contact is not always registered against its company, so relying on that
     * fetch alone leaves a real selection looking empty.
     */
    seedPeople?: ApiPerson[];
    value?: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
    /** Company name, for the create-contact form's heading. */
    companyName?: string | null;
    disabled?: boolean;
    className?: string;
}

/**
 * The contact picker used on the project form: the same PersonSelect affordance as
 * every other person field, with the company's roster loaded on demand and an
 * "Add contact" escape hatch beside it — mirroring the lead form, so a company
 * with no contacts yet is not a dead end.
 */
export function ContactAsyncSelect({
    companyId,
    seedPeople,
    value,
    onValueChange,
    placeholder,
    companyName = null,
    disabled = false,
    className,
}: ContactAsyncSelectProps) {
    const { t } = useTranslations();
    const { can } = useCan();
    const [people, setPeople] = useState<ApiPerson[]>([]);
    const [created, setCreated] = useState<ApiPerson[]>([]);

    const canCreateContacts = can('contacts.create');

    // Reset the roster when the company changes; the new one has its own contacts.
    useEffect(() => {
        setPeople([]);
        setCreated([]);
    }, [companyId]);

    useEffect(() => {
        if (!companyId) {
            return;
        }

        const controller = new AbortController();

        void (async () => {
            try {
                const response = await axios.get(`/api/companies/${companyId}/people`, { signal: controller.signal });
                setPeople((response.data?.people ?? []) as ApiPerson[]);
            } catch {
                // Leave the list empty — the field still works, just with no options.
            }
        })();

        return () => controller.abort();
    }, [companyId]);

    // Seeds and freshly created contacts first, so a prefilled or just-added contact
    // is labelled and listed even when the company's roster does not contain them.
    const options = useMemo<PersonOption[]>(() => {
        const merged = [...(seedPeople ?? []), ...created, ...people];
        const byId = new Map<string, PersonOption>();

        merged.forEach((person) => {
            if (!person?.uuid || byId.has(person.uuid)) {
                return;
            }

            byId.set(person.uuid, {
                id: person.uuid,
                first_name: person.first_name,
                last_name: person.last_name,
                role: person.position ?? null,
            });
        });

        return Array.from(byId.values());
    }, [created, people, seedPeople]);

    const handleCreated = (person: CreatedPerson) => {
        setCreated((previous) => [
            {
                uuid: person.id,
                first_name: person.first_name ?? '',
                last_name: person.last_name ?? '',
                position: (person as { position?: string }).position,
            },
            ...previous,
        ]);
        onValueChange(person.id);
    };

    return (
        <div className="space-y-1.5">
            <div className="flex items-start gap-2">
                <PersonSelect
                    people={options}
                    variant="field"
                    showRole
                    value={value || null}
                    onValueChange={(next) => onValueChange(next ?? '')}
                    disabled={disabled || !companyId}
                    className={className}
                    labels={{
                        placeholder: companyId
                            ? (placeholder ?? t('projects.create.placeholders.contact', 'Select a contact'))
                            : t('projects.create.placeholders.select_company_first', 'Select a company first'),
                        search: t('projects.create.placeholders.search_contact', 'Search contacts…'),
                        empty: t('projects.create.placeholders.no_contacts', 'This company has no contacts yet.'),
                        peopleHeading: t('projects.create.fields.contact_heading', 'Company contacts'),
                    }}
                />
                {canCreateContacts ? (
                    <CreatePersonModal
                        companyId={companyId ?? null}
                        companyName={companyName}
                        onCreated={handleCreated}
                        trigger={
                            <Button type="button" variant="outline" disabled={disabled || !companyId} className="shrink-0">
                                <UserPlus className="mr-2 h-4 w-4" />
                                {t('projects.create.buttons.add_contact', 'Add contact')}
                            </Button>
                        }
                    />
                ) : null}
            </div>

            {/* The list is a dead end for a company with no contacts yet — point at the way out. */}
            {canCreateContacts && companyId && options.length === 0 ? (
                <p className="text-muted-foreground text-xs">{t('projects.create.hints.no_contacts_add', 'This company has no contacts yet.')}</p>
            ) : null}
        </div>
    );
}
